import React, { useEffect, useState } from 'react';
import atlasLogo from '../img/atlas.png';
// CAMPAIGN 2 IS ATLAS ONLY -- restore with the b === 1 branch below.
// import inovateLogo from '../img/innovate.png';

export default function LandingPage({ nextPage, b }) {
    const [logo, setLogo] = useState("");
    const [showContent, setShowContent] = useState(false);

    // Color palette based on brand
    const colors = b === 2 ? {
        bgFrom: '#8B2500',
        bgVia: '#C4621F',
        bgTo: '#E87722',
        primary: '#F59E42',
        primaryLight: '#FFA94D',
        buttonFrom: '#F59E42',
        buttonTo: '#FFA94D',
        textAccent: '#FED7AA',
        textLight: '#FFEDD5',
        glow: 'rgba(245, 158, 66, 0.3)'
    } : {
        bgFrom: '#1A0D2E',
        bgVia: '#5D1A5E',
        bgTo: '#B81F7B',
        primary: '#EC4899',
        primaryLight: '#F472B6',
        buttonFrom: '#EC4899',
        buttonTo: '#F472B6',
        textAccent: '#F9A8D4',
        textLight: '#FBCFE8',
        glow: 'rgba(236, 72, 153, 0.3)'
    };

    useEffect(() => {
        // Atlas regardless of ?b= this campaign, so a hand-typed ?b=1 cannot
        // surface Innovate branding. Original: if (b === 1) setLogo(inovateLogo);
        setLogo(atlasLogo);
        setTimeout(() => setShowContent(true), 200);
    }, [b]);

    const handleStart = () => {
        nextPage(1);
    };

    return (
        <div 
            className='flex flex-col items-center justify-center w-full min-h-screen relative overflow-hidden'
            style={{ background: `linear-gradient(to bottom right, ${colors.bgFrom}, ${colors.bgVia}, ${colors.bgTo})` }}
        >
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-10 left-10 w-40 h-40 rounded-full blur-3xl animate-float" style={{ backgroundColor: `${colors.glow}` }}></div>
                <div className="absolute bottom-20 right-10 w-48 h-48 rounded-full blur-3xl animate-float-delayed" style={{ backgroundColor: `${colors.glow}` }}></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl animate-pulse-slow" style={{ backgroundColor: `${colors.glow}`, opacity: 0.5 }}></div>
                <div className="absolute top-1/4 right-1/4 w-32 h-32 rounded-full blur-2xl animate-float" style={{ backgroundColor: `${colors.glow}` }}></div>
                <div className="absolute bottom-1/4 left-1/4 w-36 h-36 rounded-full blur-2xl animate-float-delayed" style={{ backgroundColor: `${colors.glow}` }}></div>
            </div>

            {/* Content Container */}
            <div className={`relative z-10 flex flex-col items-center justify-center px-6 max-w-md w-full transition-all duration-1000 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                
                {/* Logo Container - Simple and Clean */}
                <div className="mb-12">
                    {logo && (
                        <img 
                            src={logo} 
                            alt="Logo" 
                            className="w-48 h-auto mx-auto"
                        />
                    )}
                </div>

                {/* Welcome Text */}
                <div className="text-center mb-12 space-y-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                    <div className="inline-block bg-white/10 backdrop-blur-sm px-6 py-3 rounded-2xl border border-white/20 shadow-lg">
                        <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-2xl">
                            Welcome!
                        </h1>
                    </div>
                </div>

                {/* Start Button */}
                <div className="w-full animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                    <button 
                        onClick={handleStart}
                        className="relative w-full text-white font-bold text-xl rounded-2xl px-10 py-6 shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 overflow-hidden group"
                        style={{ 
                            background: `linear-gradient(to right, ${colors.buttonFrom}, ${colors.buttonTo})`,
                            boxShadow: `0 20px 50px ${colors.glow}`
                        }}
                    >
                        {/* Button shimmer effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
                        
                        {/* Button content */}
                        <div className="relative z-10 flex items-center justify-center gap-3">
                            <span className="uppercase tracking-wider">Start</span>
                        </div>

                        {/* Pulsing rings */}
                        <div className="absolute inset-0 rounded-2xl">
                            <div className="absolute inset-0 rounded-2xl border-2 border-white/30 animate-ping-slow"></div>
                        </div>
                    </button>

                    {/* Hint text */}
                    <p className="text-center text-white/60 text-sm mt-4 animate-pulse">
                        Tap to start
                    </p>
                </div>
            </div>

            {/* Floating decorative elements */}
            <div className="absolute top-1/4 left-8 w-2 h-2 rounded-full animate-ping" style={{ backgroundColor: colors.primaryLight }}></div>
            <div className="absolute top-1/3 right-8 w-2 h-2 rounded-full animate-ping" style={{ backgroundColor: colors.primary, animationDelay: '0.5s' }}></div>
            <div className="absolute bottom-1/4 left-12 w-2 h-2 rounded-full animate-ping" style={{ backgroundColor: colors.primaryLight, animationDelay: '1s' }}></div>
            <div className="absolute bottom-1/3 right-12 w-2 h-2 rounded-full animate-ping" style={{ backgroundColor: colors.primary, animationDelay: '1.5s' }}></div>

            <style jsx>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px) translateX(0px); }
                    50% { transform: translateY(-20px) translateX(10px); }
                }
                @keyframes float-delayed {
                    0%, 100% { transform: translateY(0px) translateX(0px); }
                    50% { transform: translateY(25px) translateX(-15px); }
                }
                @keyframes pulse-slow {
                    0%, 100% { opacity: 0.3; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(1.1); }
                }
                @keyframes fade-in-up {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes bounce-subtle {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-8px); }
                }
                @keyframes bounce-in {
                    0% { transform: scale(0); }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); }
                }
                @keyframes ping-slow {
                    75%, 100% { transform: scale(1.1); opacity: 0; }
                }
                .animate-float { animation: float 6s ease-in-out infinite; }
                .animate-float-delayed { animation: float-delayed 8s ease-in-out infinite; }
                .animate-pulse-slow { animation: pulse-slow 4s ease-in-out infinite; }
                .animate-fade-in-up { animation: fade-in-up 0.8s ease-out forwards; }
                .animate-bounce-subtle { animation: bounce-subtle 2s ease-in-out infinite; }
                .animate-bounce-in { animation: bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards; }
                .animate-ping-slow { animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite; }
            `}</style>
        </div>
    );
}